import React, { forwardRef, useState } from "react";

import cn from "classnames";

import { TextArea } from "./components/TextArea/InputTextAreaTextArea";
import { InputTextAreaProvider, useStore } from "./store/store";
import { INPUT_TEXTAREA_DISPLAY_NAME } from "./utils/core";
import { CoreInputText } from "../../helpers/common/CoreInputText/CoreInputText";
import { useIsomorphicLayoutEffect } from "../../hooks";

import type { InputTextAreaProps } from "./types";

import classes from "./InputTextArea.styl";

const CONTAINER_HEIGHT = 86;

/**
 * Базовый компонент, который позволяет пользователю вводить текстовую информацию с клавиатуры. Отличиется от textInput высотой
 *
 * <a href="https://www.figma.com/design/qdqGHY32KlkrIwX3eppFUx/%E2%9C%8F%EF%B8%8F-Inputs-%26-Search?node-id=2647-74894">Спека</a>
 */
export const InputTextArea = forwardRef<
    HTMLTextAreaElement,
    InputTextAreaProps
>(({ maxHeight, maxLines, children, ...props }, ref) => {
    return (
        // eslint-disable-next-line ok-custom-rules/check-attribute
        <InputTextAreaProvider options={{ maxHeight, maxLines }}>
            <InputTextAreaContent {...props} ref={ref}>
                {children}
            </InputTextAreaContent>
        </InputTextAreaProvider>
    );
});

const InputTextAreaContent = forwardRef<
    HTMLTextAreaElement,
    InputTextAreaProps
>(
    (
        {
            children,
            size = "default",
            alignment = "top",
            "data-tsid": tsid,
            fixedHeight = true,
            ...props
        },
        ref,
    ) => {
        const { textAreaRef, maxHeight, maxLines } = useStore();
        const [lineHeight, setLineHeight] = useState(0);

        useIsomorphicLayoutEffect(() => {
            if (!textAreaRef.current || !maxLines || maxLines <= 0) {
                return;
            }
            const { lineHeight } = window.getComputedStyle(textAreaRef.current);
            setLineHeight(parseInt(lineHeight, 10));
        }, [maxLines, textAreaRef]);

        const inputMaxHeight = (() => {
            if (maxHeight) {
                return `${maxHeight}px`;
            }
            if (maxLines) {
                return `${maxLines * lineHeight}px`;
            }
            return `${CONTAINER_HEIGHT}px`;
        })();

        const containerClasses = (() => {
            const result = {
                [classes[`__alignment-${alignment}`]]: alignment,
            } as cn.Mapping;
            if (size && size !== "default") {
                result[classes[`__size-${size}`]] = true;
            }

            return cn(classes.textarea, result);
        })();

        const inputContainerStyles = (() => {
            if (fixedHeight) {
                return {
                    height: inputMaxHeight,
                };
            }
            return {
                maxHeight: inputMaxHeight,
            };
        })();

        const inputStyles: React.CSSProperties = {
            "--internal-input-max-height": inputMaxHeight,
        };

        return (
            <CoreInputText
                {...props}
                as={TextArea}
                inputContainerClassName={containerClasses}
                inputContainerStyle={inputContainerStyles}
                style={inputStyles}
                prefix={INPUT_TEXTAREA_DISPLAY_NAME}
                data-tsid={tsid}
                data-uikit={INPUT_TEXTAREA_DISPLAY_NAME}
                ref={ref}
            >
                {children}
            </CoreInputText>
        );
    },
);

InputTextAreaContent.displayName = "InputTextAreaContent";

InputTextArea.displayName = INPUT_TEXTAREA_DISPLAY_NAME;

export default InputTextArea;
